import { useState } from "preact/compat"
import { Button } from "@/components/ui/button"
import { getIntegrationPassword } from "@/api/integration"
import { copyToClipboard } from "@/utils/copy"

interface PasswordViewCopyButtonsProps {
  integrationId: string
  className?: string
}

export function PasswordViewCopyButtons({ integrationId, className }: PasswordViewCopyButtonsProps) {
  const [visible, setVisible] = useState(false)
  const [password, setPassword] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function fetchPassword(): Promise<string | null> {
    if (password !== null) {
      return password
    }
    setLoading(true)
    setError(null)
    const res = await getIntegrationPassword(integrationId)
    setLoading(false)
    if (!res.ok) {
      setError("Failed to load password")
      return null
    }
    const value = res.value.data.password
    setPassword(value)
    if (!value) {
      setError("No password stored for this connection")
    }
    return value
  }

  async function handleView() {
    const value = await fetchPassword()
    if (value) {
      setVisible(current => !current)
    }
  }

  async function handleCopy() {
    const value = await fetchPassword()
    if (!value) return
    await copyToClipboard(value, "Password copied to clipboard")
  }

  return (
    <div class={className}>
      <div class="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={handleView} disabled={loading}>
          {loading ? "Loading..." : visible ? "Hide password" : "View password"}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={handleCopy} disabled={loading}>
          Copy password
        </Button>
      </div>
      {error && <p class="text-sm text-red-600 mt-2">{error}</p>}
      {visible && password && (
        <p class="mt-2 text-sm font-mono text-gray-900 break-all bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
          {password}
        </p>
      )}
    </div>
  )
}
